import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Grid,
	Button,
	Autocomplete,
	TextField,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Stack,
	Chip,
	Divider,
	IconButton,
	Tooltip,
	alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import FilterListIcon from "@mui/icons-material/FilterList";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";
import { getWeeklyStats } from "../services/api";
import { useSoraFilterOptions } from "../Sora";

const defaultEnd = dayjs();
const defaultStart = dayjs().subtract(27, "day");
const MAX_DATE_RANGE_DAYS = 730;

const COLORS = {
	created: "#3b82f6",
	dispatched: "#10b981",
};

const joinFilterValues = (values) => (values?.length ? values.join(",") : "");

const buildDateOnlyFilters = (start, end) => ({
	start_date: start.format("YYYY-MM-DD"),
	end_date: end.format("YYYY-MM-DD"),
	created_by: "",
	channel: "",
	brand: "",
});

const getWeekMetric = (byWeek, weekIndex, metric) => {
	const cell = byWeek?.[weekIndex];
	if (!cell) return 0;
	return cell[metric] ?? 0;
};

const sumWeekMetric = (byWeek, weeks, metric) =>
	weeks.reduce((sum, w) => sum + getWeekMetric(byWeek, w.week_index, metric), 0);

const channelKey = (brand, channel) => `${brand}::${channel}`;

const buildPivotTree = (weeks) => {
	const brandMap = new Map();

	weeks.forEach((week) => {
		const wi = week.week_index;
		(week.drilldown ?? []).forEach((brandRow) => {
			if (!brandMap.has(brandRow.brand)) {
				brandMap.set(brandRow.brand, {
					brand: brandRow.brand,
					byWeek: {},
					channels: new Map(),
				});
			}
			const brandEntry = brandMap.get(brandRow.brand);
			brandEntry.byWeek[wi] = {
				so_created: brandRow.so_created ?? 0,
				so_dispatched: brandRow.so_dispatched ?? 0,
			};

			(brandRow.channels ?? []).forEach((ch) => {
				if (!brandEntry.channels.has(ch.channel)) {
					brandEntry.channels.set(ch.channel, {
						channel: ch.channel,
						byWeek: {},
						users: new Map(),
					});
				}
				const channelEntry = brandEntry.channels.get(ch.channel);
				channelEntry.byWeek[wi] = {
					so_created: ch.so_created ?? 0,
					so_dispatched: ch.so_dispatched ?? 0,
				};

				(ch.users ?? []).forEach((user) => {
					if (!channelEntry.users.has(user.created_by)) {
						channelEntry.users.set(user.created_by, {
							created_by: user.created_by,
							byWeek: {},
						});
					}
					const userEntry = channelEntry.users.get(user.created_by);
					userEntry.byWeek[wi] = {
						so_created: user.so_created ?? 0,
						so_dispatched: user.so_dispatched ?? 0,
					};
				});
			});
		});
	});

	return [...brandMap.values()]
		.sort(
			(a, b) => sumWeekMetric(b.byWeek, weeks, "so_created") - sumWeekMetric(a.byWeek, weeks, "so_created")
		)
		.map((brandEntry) => ({
			brand: brandEntry.brand,
			byWeek: brandEntry.byWeek,
			channels: [...brandEntry.channels.values()]
				.sort(
					(a, b) =>
						sumWeekMetric(b.byWeek, weeks, "so_created") - sumWeekMetric(a.byWeek, weeks, "so_created")
				)
				.map((channelEntry) => ({
					channel: channelEntry.channel,
					byWeek: channelEntry.byWeek,
					users: [...channelEntry.users.values()].sort(
						(a, b) =>
							sumWeekMetric(b.byWeek, weeks, "so_created") - sumWeekMetric(a.byWeek, weeks, "so_created")
					),
				})),
		}));
};

const SectionHeader = ({ icon, title, subtitle }) => (
	<Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
		<Box
			sx={{
				mt: 0.25,
				width: 36,
				height: 36,
				borderRadius: 1.5,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
				color: "primary.main",
			}}>
			{icon}
		</Box>
		<Box>
			<Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: "-0.01em" }}>
				{title}
			</Typography>
			{subtitle && (
				<Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
					{subtitle}
				</Typography>
			)}
		</Box>
	</Stack>
);

const StatCard = ({ label, value, color }) => (
	<Box
		sx={{
			flex: "1 1 180px",
			minWidth: 160,
			p: 2,
			borderRadius: 2,
			border: "1px solid",
			borderColor: "divider",
			background: (theme) =>
				`linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${theme.palette.background.paper} 70%)`,
		}}>
		<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
			{label}
		</Typography>
		<Typography variant="h5" sx={{ fontWeight: 700, color, mt: 0.5, letterSpacing: "-0.02em" }}>
			{value.toLocaleString()}
		</Typography>
	</Box>
);

const MetricCell = ({ value, variant, isTotalColumn }) => (
	<TableCell
		align="right"
		sx={{
			...metricColSx,
			fontVariantNumeric: "tabular-nums",
			fontSize: "0.8125rem",
			...(variant === "created" && {
				color: COLORS.created,
				bgcolor: alpha(COLORS.created, isTotalColumn ? 0.1 : 0.04),
			}),
			...(variant === "dispatched" && {
				color: COLORS.dispatched,
				bgcolor: alpha(COLORS.dispatched, isTotalColumn ? 0.1 : 0.04),
			}),
		}}>
		{value.toLocaleString()}
	</TableCell>
);

const DispatchPctChip = ({ created, dispatched }) => {
	if (!created) {
		return <Chip label="—" size="small" variant="outlined" sx={{ height: 22, fontSize: "0.75rem" }} />;
	}
	const pct = Math.round((dispatched / created) * 100);
	const color = pct >= 80 ? "success" : pct >= 50 ? "warning" : "default";
	return <Chip label={`${pct}%`} size="small" color={color} variant="outlined" sx={{ height: 22, fontWeight: 600, fontSize: "0.75rem" }} />;
};

const METRIC_COL_MIN = 92;
const WEEK_GROUP_MIN = METRIC_COL_MIN * 2;
const HIERARCHY_MIN_WIDTH = 520;
/** Height of pivot header row 1 (week chips + date labels); row 2 sticks below this */
const PIVOT_HEADER_ROW1_HEIGHT = 84;
const PIVOT_STICKY_Z = 10;

const headerCellSx = {
	fontWeight: 700,
	fontSize: "0.75rem",
	textTransform: "uppercase",
	letterSpacing: "0.04em",
	bgcolor: "grey.50",
	borderBottom: "1px solid",
	borderColor: "divider",
	whiteSpace: "nowrap",
};

const stickyHeadRow1 = (extra = {}) => ({
	...headerCellSx,
	position: "sticky",
	top: 0,
	zIndex: PIVOT_STICKY_Z,
	backgroundColor: "#fafafa",
	boxShadow: "0 1px 0 rgba(0,0,0,0.1)",
	...extra,
});

const stickyHeadRow2 = (extra = {}) => ({
	...headerCellSx,
	position: "sticky",
	top: PIVOT_HEADER_ROW1_HEIGHT,
	zIndex: PIVOT_STICKY_Z,
	backgroundColor: "#fafafa",
	boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
	borderBottom: "2px solid",
	borderColor: "divider",
	...extra,
});

const weekHeaderCellSx = (row, extra = {}) =>
	row === 1
		? stickyHeadRow1({ minWidth: WEEK_GROUP_MIN, width: WEEK_GROUP_MIN, verticalAlign: "top", textAlign: "center", ...extra })
		: stickyHeadRow2({ minWidth: WEEK_GROUP_MIN, width: WEEK_GROUP_MIN, textAlign: "center", ...extra });

const totalHeaderCellSx = (row, extra = {}) =>
	row === 1
		? stickyHeadRow1({
				minWidth: WEEK_GROUP_MIN,
				width: WEEK_GROUP_MIN,
				backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
				textAlign: "center",
				...extra,
			})
		: stickyHeadRow2({
				minWidth: WEEK_GROUP_MIN,
				width: WEEK_GROUP_MIN,
				backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
				textAlign: "center",
				...extra,
			});

const metricColSx = {
	minWidth: METRIC_COL_MIN,
	width: METRIC_COL_MIN,
};

const PivotBrandCell = ({ brand }) => (
	<Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
		<Box sx={{ width: 4, height: 20, borderRadius: 1, bgcolor: COLORS.created, flexShrink: 0 }} />
		<Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
			{brand}
		</Typography>
	</Stack>
);

const ExpandToggle = ({ expanded, onClick, disabled, label }) => (
	<IconButton
		size="small"
		onClick={onClick}
		disabled={disabled}
		aria-label={label}
		aria-expanded={expanded}
		sx={{
			p: 0.25,
			color: "text.secondary",
			"&:hover": { color: "primary.main", bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08) },
		}}>
		{expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
	</IconButton>
);

const PivotMetricCells = ({ byWeek, weeks, fontWeight }) => (
	<>
		<MetricCell
			value={sumWeekMetric(byWeek, weeks, "so_created")}
			variant="created"
			isTotalColumn
		/>
		<MetricCell
			value={sumWeekMetric(byWeek, weeks, "so_dispatched")}
			variant="dispatched"
			isTotalColumn
		/>
		{weeks.map((week) => (
			<React.Fragment key={week.week_index}>
				<MetricCell value={getWeekMetric(byWeek, week.week_index, "so_created")} variant="created" />
				<MetricCell
					value={getWeekMetric(byWeek, week.week_index, "so_dispatched")}
					variant="dispatched"
				/>
			</React.Fragment>
		))}
	</>
);

const buildGrandTotalByWeek = (weeks) => {
	const byWeek = {};
	weeks.forEach((week) => {
		byWeek[week.week_index] = {
			so_created: week.so_created ?? 0,
			so_dispatched: week.so_dispatched ?? 0,
		};
	});
	return byWeek;
};

const WeeklyPivotTable = ({ weeks }) => {
	const pivotTree = useMemo(() => buildPivotTree(weeks), [weeks]);
	const [expandedBrands, setExpandedBrands] = useState(() => new Set());
	const [expandedChannels, setExpandedChannels] = useState(() => new Set());

	useEffect(() => {
		setExpandedBrands(new Set());
		setExpandedChannels(new Set());
	}, [weeks]);

	const toggleBrand = (brand) => {
		setExpandedBrands((prev) => {
			const next = new Set(prev);
			if (next.has(brand)) next.delete(brand);
			else next.add(brand);
			return next;
		});
	};

	const toggleChannel = (brand, channel) => {
		const key = channelKey(brand, channel);
		setExpandedChannels((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const expandAll = () => {
		setExpandedBrands(new Set(pivotTree.map((b) => b.brand)));
		setExpandedChannels(
			new Set(
				pivotTree.flatMap((b) => b.channels.map((ch) => channelKey(b.brand, ch.channel)))
			)
		);
	};

	const collapseAll = () => {
		setExpandedBrands(new Set());
		setExpandedChannels(new Set());
	};

	const grandTotalByWeek = useMemo(() => buildGrandTotalByWeek(weeks), [weeks]);
	const tableMinWidth = HIERARCHY_MIN_WIDTH + WEEK_GROUP_MIN * (weeks.length + 1);

	if (pivotTree.length === 0) {
		return (
			<Box
				sx={{
					py: 4,
					textAlign: "center",
					borderRadius: 2,
					border: "1px dashed",
					borderColor: "divider",
					bgcolor: "grey.50",
				}}>
				<Typography variant="body2" color="text.secondary">
					No drill-down data for the selected filters.
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Stack direction="row" spacing={1} sx={{ mb: 1.5, justifyContent: "flex-end" }}>
				<Button
					size="small"
					variant="outlined"
					startIcon={<UnfoldMoreIcon />}
					onClick={expandAll}
					sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.8125rem" }}>
					Expand all
				</Button>
				<Button
					size="small"
					variant="outlined"
					startIcon={<UnfoldLessIcon />}
					onClick={collapseAll}
					sx={{ textTransform: "none", borderRadius: 2, fontSize: "0.8125rem" }}>
					Collapse all
				</Button>
			</Stack>
			<TableContainer
				sx={{
					maxHeight: 520,
					borderRadius: 2,
					border: "1px solid",
					borderColor: "divider",
					overflow: "auto",
					bgcolor: "background.paper",
					position: "relative",
					"& .MuiTableHead-root .MuiTableCell-root": {
						backgroundClip: "padding-box",
					},
				}}>
			<Table
				size="small"
				sx={{
					tableLayout: "fixed",
					width: tableMinWidth,
					minWidth: "100%",
					borderCollapse: "separate",
					borderSpacing: 0,
				}}>
				<TableHead
					sx={{
						"& .MuiTableCell-head": {
							backgroundColor: "#fafafa",
						},
					}}>
					<TableRow>
						<TableCell
							colSpan={3}
							sx={stickyHeadRow1({
								minWidth: HIERARCHY_MIN_WIDTH,
								width: HIERARCHY_MIN_WIDTH,
								height: PIVOT_HEADER_ROW1_HEIGHT,
								verticalAlign: "middle",
							})}>
							Hierarchy
						</TableCell>
						<TableCell colSpan={2} sx={totalHeaderCellSx(1)}>
							<Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: "0.06em" }}>
								TOTAL
							</Typography>
						</TableCell>
						{weeks.map((week) => (
							<TableCell key={week.week_index} colSpan={2} sx={weekHeaderCellSx(1)}>
								<Tooltip title={week.label} placement="top">
									<Box sx={{ display: "inline-block", width: "100%" }}>
										<Chip
											label={`W${week.week_index + 1}`}
											size="small"
											sx={{ fontWeight: 700, mb: 0.75, height: 22 }}
										/>
										<Typography
											component="div"
											variant="caption"
											color="text.secondary"
											sx={{
												fontWeight: 400,
												lineHeight: 1.35,
												fontSize: "0.68rem",
												whiteSpace: "normal",
												wordBreak: "break-word",
												px: 0.5,
											}}>
											{week.label}
										</Typography>
									</Box>
								</Tooltip>
							</TableCell>
						))}
					</TableRow>
					<TableRow>
						<TableCell sx={stickyHeadRow2({ minWidth: 180, fontSize: "0.7rem" })}>Brand</TableCell>
						<TableCell sx={stickyHeadRow2({ minWidth: 160, fontSize: "0.7rem" })}>Channel</TableCell>
						<TableCell sx={stickyHeadRow2({ minWidth: 140, fontSize: "0.7rem" })}>Created By</TableCell>
						<TableCell align="right" sx={totalHeaderCellSx(2, { color: COLORS.created, fontSize: "0.7rem" })}>
							Created
						</TableCell>
						<TableCell align="right" sx={totalHeaderCellSx(2, { color: COLORS.dispatched, fontSize: "0.7rem" })}>
							Dispatched
						</TableCell>
						{weeks.map((week) => (
							<React.Fragment key={`sub-${week.week_index}`}>
								<TableCell
									align="right"
									sx={weekHeaderCellSx(2, { color: COLORS.created, fontSize: "0.7rem" })}>
									Created
								</TableCell>
								<TableCell
									align="right"
									sx={weekHeaderCellSx(2, { color: COLORS.dispatched, fontSize: "0.7rem" })}>
									Dispatched
								</TableCell>
							</React.Fragment>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow
						sx={{
							bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
							borderBottom: "2px solid",
							borderColor: "divider",
						}}>
						<TableCell colSpan={3} sx={{ fontWeight: 800, fontSize: "0.875rem" }}>
							Grand Total
						</TableCell>
						<PivotMetricCells byWeek={grandTotalByWeek} weeks={weeks} fontWeight={800} />
					</TableRow>

					{pivotTree.map((brandNode) => {
						const brandExpanded = expandedBrands.has(brandNode.brand);
						const hasChannels = brandNode.channels.length > 0;

						return (
							<React.Fragment key={brandNode.brand}>
								<TableRow
									hover
									sx={{
										bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
									}}>
									<TableCell>
										<Stack direction="row" alignItems="center" spacing={0.5}>
											<ExpandToggle
												expanded={brandExpanded}
												onClick={() => toggleBrand(brandNode.brand)}
												disabled={!hasChannels}
												label={`${brandExpanded ? "Collapse" : "Expand"} ${brandNode.brand}`}
											/>
											<PivotBrandCell brand={brandNode.brand} />
										</Stack>
									</TableCell>
									<TableCell>
										<Typography variant="caption" color="text.disabled">
											{hasChannels ? `${brandNode.channels.length} channel(s)` : "—"}
										</Typography>
									</TableCell>
									<TableCell>
										<Typography variant="caption" color="text.disabled">
											—
										</Typography>
									</TableCell>
									<PivotMetricCells byWeek={brandNode.byWeek} weeks={weeks} fontWeight={700} />
								</TableRow>

								{brandExpanded &&
									brandNode.channels.map((channelNode) => {
										const chKey = channelKey(brandNode.brand, channelNode.channel);
										const channelExpanded = expandedChannels.has(chKey);
										const hasUsers = channelNode.users.length > 0;

										return (
											<React.Fragment key={chKey}>
												<TableRow
													hover
													sx={{
														bgcolor: (theme) => alpha(theme.palette.text.primary, 0.02),
													}}>
													<TableCell>
														<Typography variant="caption" color="text.disabled">
															—
														</Typography>
													</TableCell>
													<TableCell>
														<Stack direction="row" alignItems="center" spacing={0.5} sx={{ pl: 2.5 }}>
															<ExpandToggle
																expanded={channelExpanded}
																onClick={() =>
																	toggleChannel(brandNode.brand, channelNode.channel)
																}
																disabled={!hasUsers}
																label={`${channelExpanded ? "Collapse" : "Expand"} ${channelNode.channel}`}
															/>
															<Typography variant="body2" sx={{ fontWeight: 600 }}>
																{channelNode.channel}
															</Typography>
														</Stack>
													</TableCell>
													<TableCell>
														<Typography variant="caption" color="text.disabled">
															{hasUsers ? `${channelNode.users.length} user(s)` : "—"}
														</Typography>
													</TableCell>
													<PivotMetricCells
														byWeek={channelNode.byWeek}
														weeks={weeks}
														fontWeight={600}
													/>
												</TableRow>

												{channelExpanded &&
													channelNode.users.map((userNode) => (
														<TableRow
															key={`${chKey}-${userNode.created_by}`}
															hover
															sx={{ bgcolor: "grey.50" }}>
															<TableCell />
															<TableCell>
																<Typography
																	variant="body2"
																	sx={{ pl: 5, color: "text.secondary", fontSize: "0.8rem" }}>
																	{channelNode.channel}
																</Typography>
															</TableCell>
															<TableCell>
																<Typography variant="body2" sx={{ pl: 2.5 }}>
																	{userNode.created_by}
																</Typography>
															</TableCell>
															<PivotMetricCells
																byWeek={userNode.byWeek}
																weeks={weeks}
																fontWeight={400}
															/>
														</TableRow>
													))}
											</React.Fragment>
										);
									})}
							</React.Fragment>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
		</Box>
	);
};

const filterFieldSx = {
	"& .MuiOutlinedInput-root": {
		borderRadius: 2,
		bgcolor: "background.paper",
	},
};

const SalesOrderWeeklyReport = () => {
	const { data: filterOptions, loading: filterOptionsLoading } = useSoraFilterOptions();

	const [startDate, setStartDate] = useState(defaultStart);
	const [endDate, setEndDate] = useState(defaultEnd);
	const [createdBy, setCreatedBy] = useState([]);
	const [channel, setChannel] = useState([]);
	const [brand, setBrand] = useState([]);

	const [appliedFilters, setAppliedFilters] = useState(() =>
		buildDateOnlyFilters(defaultStart, defaultEnd)
	);

	const [reportData, setReportData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const createdByOptions = filterOptions?.created_by ?? [];
	const channelOptions = filterOptions?.channel ?? [];
	const brandOptions = filterOptions?.brand ?? [];

	const fetchReport = useCallback(async (filters) => {
		setLoading(true);
		setError(null);
		try {
			const response = await getWeeklyStats(filters);
			setReportData(response.data);
		} catch (err) {
			console.error("Failed to fetch weekly SO stats:", err);
			setReportData(null);
			setError(err?.response?.data?.error || err?.message || "Failed to load weekly report");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchReport(appliedFilters);
	}, [appliedFilters, fetchReport]);

	const handleApply = () => {
		if (!startDate || !endDate) {
			setError("Start date and end date are required");
			return;
		}
		if (startDate.isAfter(endDate)) {
			setError("Start date must be on or before end date");
			return;
		}
		const rangeDays = endDate.diff(startDate, "day");
		if (rangeDays > MAX_DATE_RANGE_DAYS) {
			setError("Date range cannot exceed 2 years (730 days)");
			return;
		}
		setError(null);
		setAppliedFilters({
			...buildDateOnlyFilters(startDate, endDate),
			created_by: joinFilterValues(createdBy),
			channel: joinFilterValues(channel),
			brand: joinFilterValues(brand),
		});
	};

	const weeks = reportData?.weeks ?? [];

	const chartLabels = useMemo(
		() => weeks.map((w) => `W${w.week_index + 1}`),
		[weeks]
	);

	return (
		<Card
			elevation={0}
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 3,
				overflow: "hidden",
				mt: 4,
				boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
			}}>
			<CardContent sx={{ p: 0 }}>
				<Box
					sx={{
						px: 3,
						py: 2.5,
						background: (theme) =>
							`linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${theme.palette.background.paper} 100%)`,
						borderBottom: "1px solid",
						borderColor: "divider",
					}}>
					<Typography
						variant="h6"
						sx={{
							fontWeight: 700,
							fontSize: "1rem",
							letterSpacing: "-0.02em",
							color: "text.primary",
							mb: 0.5,
						}}>
						Weekly Sales Order Summary
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem", maxWidth: 720, lineHeight: 1.5 }}>
						Track SO volume by week with brand, channel, and user drill-down. Empty filters include all values (up to 2 years).
					</Typography>
				</Box>

				<Box sx={{ px: 3, py: 3, position: "relative" }}>
					{filterOptionsLoading && (
						<Box
							sx={{
								position: "absolute",
								inset: 0,
								bgcolor: "rgba(255,255,255,0.75)",
								backdropFilter: "blur(4px)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								zIndex: 2,
								borderRadius: 2,
							}}>
							<CircularProgress size={32} />
						</Box>
					)}

					<Paper
						elevation={0}
						sx={{
							p: 2.5,
							mb: 3,
							borderRadius: 2.5,
							border: "1px solid",
							borderColor: "divider",
							bgcolor: "grey.50",
						}}>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
							<FilterListIcon fontSize="small" color="action" />
							<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
								Filters
							</Typography>
						</Stack>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={12} sm={6} md={2}>
									<DatePicker
										label="Start Date"
										value={startDate}
										onChange={(v) => setStartDate(v)}
										format="DD-MM-YYYY"
										slotProps={{ textField: { size: "small", fullWidth: true, sx: filterFieldSx } }}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<DatePicker
										label="End Date"
										value={endDate}
										onChange={(v) => setEndDate(v)}
										format="DD-MM-YYYY"
										slotProps={{ textField: { size: "small", fullWidth: true, sx: filterFieldSx } }}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<Autocomplete
										multiple
										size="small"
										options={createdByOptions}
										value={createdBy}
										onChange={(_, v) => setCreatedBy(v)}
										renderInput={(params) => (
											<TextField {...params} label="Created By" placeholder="All" sx={filterFieldSx} />
										)}
										limitTags={1}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<Autocomplete
										multiple
										size="small"
										options={channelOptions}
										value={channel}
										onChange={(_, v) => setChannel(v)}
										renderInput={(params) => (
											<TextField {...params} label="Channel" placeholder="All" sx={filterFieldSx} />
										)}
										limitTags={1}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<Autocomplete
										multiple
										size="small"
										options={brandOptions}
										value={brand}
										onChange={(_, v) => setBrand(v)}
										renderInput={(params) => (
											<TextField {...params} label="Brand" placeholder="All" sx={filterFieldSx} />
										)}
										limitTags={1}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<Button
										variant="contained"
										fullWidth
										onClick={handleApply}
										disabled={loading}
										sx={{
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 600,
											py: 1,
											boxShadow: "none",
											"&:hover": { boxShadow: 1 },
										}}>
										{loading ? "Loading…" : "Apply filters"}
									</Button>
								</Grid>
							</Grid>
						</LocalizationProvider>
					</Paper>

					{error && (
						<Paper
							elevation={0}
							sx={{
								p: 1.5,
								mb: 2,
								borderRadius: 2,
								bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
								border: "1px solid",
								borderColor: "error.light",
							}}>
							<Typography color="error" sx={{ fontSize: "0.875rem" }}>
								{error}
							</Typography>
						</Paper>
					)}

					{loading && (
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
							<CircularProgress size={40} />
							<Typography variant="body2" color="text.secondary">
								Building weekly report…
							</Typography>
						</Box>
					)}

					{!loading && !error && reportData && (
						<>
							{reportData.totals && (
								<Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap" }}>
									<StatCard
										label="Total SO Created"
										value={reportData.totals.so_created ?? 0}
										color={COLORS.created}
									/>
									<StatCard
										label="Total SO Dispatched"
										value={reportData.totals.so_dispatched ?? 0}
										color={COLORS.dispatched}
									/>
								</Stack>
							)}

							{weeks.length === 0 ? (
								<Box
									sx={{
										py: 6,
										textAlign: "center",
										borderRadius: 2,
										border: "1px dashed",
										borderColor: "divider",
									}}>
									<Typography color="text.secondary">No data for the selected filters.</Typography>
								</Box>
							) : (
								<>
									<SectionHeader
										icon={<BarChartIcon fontSize="small" />}
										title="Weekly trend"
										subtitle="SO created vs dispatched by 7-day bucket"
									/>
									<Box
										sx={{
											p: 2.5,
											borderRadius: 2.5,
											border: "1px solid",
											borderColor: "divider",
											mb: 4,
											bgcolor: "background.paper",
											boxShadow: (theme) => `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
										}}>
										<BarChart
											height={300}
											borderRadius={6}
											xAxis={[
												{
													scaleType: "band",
													data: chartLabels,
													tickLabelStyle: { fontSize: 12, fontWeight: 500 },
												},
											]}
											series={[
												{
													id: "so_created",
													label: "SO Created",
													data: weeks.map((w) => w.so_created),
													color: COLORS.created,
												},
												{
													id: "so_dispatched",
													label: "SO Dispatched",
													data: weeks.map((w) => w.so_dispatched),
													color: COLORS.dispatched,
												},
											]}
											margin={{ bottom: 56, top: 16 }}
											slotProps={{
												legend: {
													direction: "row",
													position: { vertical: "bottom", horizontal: "middle" },
													labelStyle: { fontSize: 13, fontWeight: 500 },
												},
											}}
											sx={{
												"& .MuiChartsAxis-line": { stroke: "divider" },
												"& .MuiChartsGrid-line": { strokeDasharray: "4 4", opacity: 0.4 },
											}}
										/>
									</Box>

									<Divider sx={{ mb: 3 }} />

									<SectionHeader
										icon={<TableChartIcon fontSize="small" />}
										title="Weekly summary"
										subtitle="One row per week with dispatch rate"
									/>
									<TableContainer
										component={Paper}
										elevation={0}
										sx={{ borderRadius: 2.5, border: "1px solid", borderColor: "divider", mb: 4 }}>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell sx={headerCellSx}>Week</TableCell>
													<TableCell sx={headerCellSx}>Period</TableCell>
													<TableCell align="right" sx={{ ...headerCellSx, color: COLORS.created }}>
														SO Created
													</TableCell>
													<TableCell align="right" sx={{ ...headerCellSx, color: COLORS.dispatched }}>
														SO Dispatched
													</TableCell>
													<TableCell align="right" sx={headerCellSx}>
														% Dispatched
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{weeks.map((week, i) => (
													<TableRow
														key={week.week_index}
														hover
														sx={{ bgcolor: i % 2 === 0 ? "transparent" : "grey.50" }}>
														<TableCell>
															<Chip label={`W${week.week_index + 1}`} size="small" variant="outlined" sx={{ fontWeight: 700 }} />
														</TableCell>
														<TableCell sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
															{week.label}
														</TableCell>
														<TableCell align="right" sx={{ fontWeight: 600, color: COLORS.created, fontVariantNumeric: "tabular-nums" }}>
															{week.so_created?.toLocaleString() ?? 0}
														</TableCell>
														<TableCell align="right" sx={{ fontWeight: 600, color: COLORS.dispatched, fontVariantNumeric: "tabular-nums" }}>
															{week.so_dispatched?.toLocaleString() ?? 0}
														</TableCell>
														<TableCell align="right">
															<DispatchPctChip created={week.so_created} dispatched={week.so_dispatched} />
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>

									<SectionHeader
										icon={<AccountTreeIcon fontSize="small" />}
										title="Drill-down pivot"
										subtitle="Expand brands and channels to see Channel and Created By breakdown"
									/>
									<WeeklyPivotTable weeks={weeks} />
								</>
							)}
						</>
					)}
				</Box>
			</CardContent>
		</Card>
	);
};

export default SalesOrderWeeklyReport;
